import { Button, Drawer, Field, Input, Portal } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFilter } from 'react-icons/fa6';

import { Route } from '@/routes/_layout/$slug/access-management';

const FilterUsers = () => {
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation();
  const { filter } = Route.useSearch();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({
    email: '',
    first_name: '',
    last_name: '',
  });

  useEffect(() => {
    if (filter) {
      const parsedFilters: { [key: string]: any } = {};

      // Split the filter parameter (e.g., name=task&priority=high)
      const filterParams = new URLSearchParams(filter);

      // Populate the filters state with the values from the query parameter
      filterParams.forEach((value, key) => {
        if (key in filters) {
          if (key === 'priority' || key === 'archived' || key === 'locked' || key === 'impact') {
            parsedFilters[key] = value.split(','); // Handle multi-value filters
          } else {
            parsedFilters[key] = value;
          }
        }
      });

      // Update the filters state
      setFilters((prev) => ({
        ...prev,
        ...parsedFilters,
      }));
    }
  }, [filter]);

  const handleFilterChange = (column: string, value: any) => {
    setFilters((prev) => ({ ...prev, [column]: value }));
  };

  const applyFilters = () => {
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && JSON.stringify(value) !== JSON.stringify(['all']))
    );
    const encodedFilter = new URLSearchParams(
      Object.entries(cleanedFilters).map(([key, value]) => {
        if (Array.isArray(value)) {
          return [key, value.join(',')];
        }
        return [key, value];
      })
    ).toString();

    navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        filter: encodedFilter.replace(/\+/g, '%20'),
      }),
    });
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    // Vérifie si le tiroir est ouvert et qu'on écoute les événements de clavier
    if (isDrawerOpen) {
      const handleKeyDown = (event: { key: string }) => {
        if (event.key === 'Enter') {
          applyFilters(); // Appelle la fonction pour appliquer les filtres
        }
      };

      // Ajoute l'écouteur d'événements
      window.addEventListener('keydown', handleKeyDown);

      // Nettoyage de l'écouteur lorsqu'on ferme le tiroir
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isDrawerOpen, filters]);

  return (
    <>
      <Drawer.Root
        open={isDrawerOpen}
        onOpenChange={(e) => setIsDrawerOpen(e.open)}
        size="sm"
        placement="end"
        onEscapeKeyDown={() => applyFilters()}
      >
        <Drawer.Trigger asChild>
          <Button variant="outline" size="sm" float={'right'}>
            <FaFilter /> {t('ui.filter')}
          </Button>
        </Drawer.Trigger>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content
              rounded="md"
              position={'absolute'}
              margin={'{spacing.4}'}
              h={'calc(100% - {spacing.4} * 2)'}
              right={0}
              top={0}
            >
              <Drawer.CloseTrigger />
              <Drawer.Header>{t('user.filter.title')}</Drawer.Header>
              <Drawer.Body>
                {/* Email Filter */}
                <Field.Root>
                  <Field.Label>{t('user.filter.email.contains')}</Field.Label>
                  <Input
                    placeholder={t('user.filter.email.contains.placeholder')}
                    value={filters.email}
                    onChange={(e) => handleFilterChange('email', e.target.value)}
                  />
                </Field.Root>

                {/* First name Filter */}
                <Field.Root mt={4}>
                  <Field.Label>{t('user.filter.firstName.contains')}</Field.Label>
                  <Input
                    placeholder={t('user.filter.firstName.contains.placeholder')}
                    value={filters.first_name}
                    onChange={(e) => handleFilterChange('first_name', e.target.value)}
                  />
                </Field.Root>

                <Field.Root mt={4}>
                  <Field.Label>{t('user.filter.lastName.contains')}</Field.Label>
                  <Input
                    placeholder={t('user.filter.lastName.contains.placeholder')}
                    value={filters.last_name}
                    onChange={(e) => handleFilterChange('last_name', e.target.value)}
                  />
                </Field.Root>
              </Drawer.Body>
              <Drawer.Footer>
                <Button variant="outline" mr={3} onClick={() => setIsDrawerOpen(false)}>
                  {t('ui.cancel')}
                </Button>
                <Button colorScheme="blue" onClick={applyFilters}>
                  {t('ui.apply.filter')}
                </Button>
              </Drawer.Footer>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </>
  );
};

export default FilterUsers;
